
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import SalesPurReport from "./SalesPurReport";

const SalesPurchaseMis = ({ setEditingContext }) => {
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0];

  const [reportTypes, setReportTypes] = useState([]);
  const [reportType, setReportType] = useState("");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState("filter"); // "filter" | "report"
  const [reportData, setReportData] = useState([]);

  // ✅ Fetch report types when component mounts
  useEffect(() => {
    const fetchReportTypes = async () => {
      try {
        setLoading(true);
        const username = user?.username || "admin";
        const result = await api.Gettype(username);
        const data = result?.Table || [];
        setReportTypes(data);
      } catch (error) {
        console.error("❌ Error fetching report types:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReportTypes();
  }, [user]);

  // ✅ Handle preview/report fetch
  const handlePreview = async () => {
    try {
      setLoading(true);
      const username = user?.username || "admin";

      const result = await api.SalesPurchaseMIS(
        reportType,
        username,
        fromDate,
        toDate
      );

      const data = (result?.Table || []).map((item) => ({
        ...item,
        invdate: item.invdate ? item.invdate.split("T")[0] : "",
        NetAmount: Number(item.NetAmount || 0),
        Qty: Number(item.Qty || 0),
        Rate: Number(item.Rate || 0),
      }));

      console.log("✅ Fetched report data:", data);

      if (typeof setEditingContext === "function") {
        setEditingContext({
          reportType,
          fromDate,
          toDate,
          data,
        });
      }

      setReportData(data);
      setCurrentView("report");
    } catch (error) {
      console.error("❌ Error fetching MIS report:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle going back to filter
  const handleBack = () => {
    setCurrentView("filter");
    setReportData([]);
  };

  // ✅ Decide report title dynamically
  const reportTitle =
    reportType.toLowerCase() === "sales"
      ? "Interactive Sales Report"
      : reportType.toLowerCase() === "purchase"
      ? "Interactive Purchase Report"
      : "Interactive Report";

  return (
    <div className="flex flex-col items-center mt-12 px-4">
      {currentView === "filter" && (
        <div className="bg-white p-8 rounded-xl shadow-md w-full max-w-md text-center">
          <h1 className="w-full text-center mb-6 text-2xl sm:text-3xl font-bold text-gray-800">
            Sales/Purchase-MIS
          </h1>

          {/* Report Type */}
          <div className="mb-4 text-left">
            <label className="block mb-2 font-medium text-gray-800">
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 py-2 border border-black rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
            >
              <option value="">Select Report</option>
              {loading ? (
                <option disabled>Loading...</option>
              ) : (
                reportTypes.map((item, index) => (
                  <option key={index} value={item.TxnType}>
                    {item.TxnType}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* From Date */}
          <div className="mb-4 text-left">
            <label className="block mb-2 font-medium text-gray-800">
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 border border-black rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
            />
          </div>

          {/* To Date */}
          <div className="mb-6 text-left">
            <label className="block mb-2 font-medium text-gray-800">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 border border-black rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
            />
          </div>

          {/* Preview Button */}
          <button
            onClick={handlePreview}
            disabled={loading || !reportType}
            className="w-full py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {loading ? "Loading..." : "Preview"}
          </button>
        </div>
      )}

      {currentView === "report" && (
        <SalesPurReport
          reportType={reportType}
          reportData={reportData}
          title={reportTitle}  // ✅ Pass dynamic title
          onBack={handleBack}
        />
      )}
    </div>
  );
};

export default SalesPurchaseMis;
